import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, ExternalLink, FileText } from "lucide-react";
import { useCourseDetailsQuery } from "../api/studentCoursesApi";
import { tokenService } from "../services/TokenService";
import routes from "../routes";
import ModuleContentRenderer from "../components/modules/ModuleContentRenderer";

const PageContentPage: React.FC = () => {
  const { contextId } = useParams<{ contextId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const moduleId = searchParams.get("moduleId");
  const moduleName = searchParams.get("moduleName");
  const modname = searchParams.get("modname") || "page"; // Get module type from URL
  const token = tokenService.getToken();

  const { data: courseDetails } = useCourseDetailsQuery(contextId, token);

  // Find the module in course details
  const module = React.useMemo(() => {
    if (!courseDetails || !moduleId) return null;

    for (const section of courseDetails.sections) {
      const found = section.modules.find((m) => m.id.toString() === moduleId);
      if (found) return found;
    }
    return null;
  }, [courseDetails, moduleId]);

  const handleBack = () => {
    navigate(`/my-courses/course-details/${contextId}`);
  };

  const handleOpenInMoodle = () => {
    if (module?.url) {
      window.open(module.url, "_blank");
    }
  };

  if (!contextId || !moduleId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Invalid page parameters</p>
          <Button onClick={handleBack} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex bg-gray-50 overflow-hidden z-50">
      {/* Left Sidebar - Course Navigation (Fixed) */}
      <aside className="w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
        {/* Sidebar Header */}
        <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="flex items-center gap-2 mb-3 -ml-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Course
          </Button>
          <h1 className="text-base font-semibold text-gray-900 truncate">
            {courseDetails?.courseTitle || "Course"}
          </h1>
        </div>

        {/* Course Contents List */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-8 py-6 border-b border-gray-200 bg-white sticky top-0 z-10">
            <h3 className="text-sm font-semibold text-gray-900">
              Course Contents
            </h3>
          </div>
          <div className="py-3 px-3 space-y-1">
            {courseDetails?.sections.map((section) => (
              <div key={section.id}>
                {section.modules.filter((m) => m.modname === "page").length >
                  0 && (
                  <>
                    {section.name && (
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2 mt-2 first:mt-0">
                        {section.name}
                      </div>
                    )}
                    {section.modules.map((mod) => {
                      const isActive = mod.id.toString() === moduleId;
                      const isPage = mod.modname === "page";

                      if (!isPage) return null;

                      return (
                        <button
                          key={mod.id}
                          onClick={() => {
                            if (isActive) return;
                            navigate(
                              routes.toPageContent(
                                contextId || "",
                                mod.id.toString(),
                                mod.name,
                              ),
                            );
                          }}
                          className={`w-full text-left px-3 py-3 rounded-lg text-sm transition-all ${
                            isActive
                              ? "bg-blue-600 text-white font-medium shadow-md"
                              : "text-gray-700 hover:bg-gray-50 hover:shadow-sm"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <FileText
                              className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                                isActive ? "text-white" : "text-gray-500"
                              }`}
                            />
                            <span className="line-clamp-2 leading-relaxed">
                              {mod.name}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {module?.name || moduleName || "Page Content"}
            </h2>
            {module?.url && (
              <Button
                onClick={handleOpenInMoodle}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Open in Moodle
              </Button>
            )}
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <ModuleContentRenderer
            contextId={contextId || ""}
            moduleId={moduleId || ""}
            modname={modname}
            module={module}
          />
        </div>
      </main>
    </div>
  );
};

export default PageContentPage;
